/**
 * MIT License
 *
 * Copyright (c) 2023–Present PPResume (https://ppresume.com)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */

import type { Country } from '@/models'

/**
 * All possible countries and regions in the world.
 */
export const COUNTRY_OPTIONS = [
  'Afghanistan',
  'Aland Islands',
  'Albania',
  'Algeria',
  'American Samoa',
  'Andorra',
  'Angola',
  'Anguilla',
  'Antarctica',
  'Antigua And Barbuda',
  'Argentina',
  'Armenia',
  'Aruba',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bermuda',
  'Bhutan',
  'Bolivia',
  'Bonaire, Sint Eustatius and Saba',
  'Bosnia and Herzegovina',
  'Botswana',
  'Bouvet Island',
  'Brazil',
  'British Indian Ocean Territory',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Cape Verde',
  'Cayman Islands',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Christmas Island',
  'Cocos (Keeling) Islands',
  'Colombia',
  'Comoros',
  'Congo',
  'Cook Islands',
  'Costa Rica',
  "Cote D'Ivoire (Ivory Coast)",
  'Croatia',
  'Cuba',
  'Curaçao',
  'Cyprus',
  'Czech Republic',
  'Democratic Republic of the Congo',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'East Timor',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Ethiopia',
  'Falkland Islands',
  'Faroe Islands',
  'Fiji Islands',
  'Finland',
  'France',
  'French Guiana',
  'French Polynesia',
  'French Southern Territories',
  'Gabon',
  'Gambia The',
  'Georgia',
  'Germany',
  'Ghana',
  'Gibraltar',
  'Greece',
  'Greenland',
  'Grenada',
  'Guadeloupe',
  'Guam',
  'Guatemala',
  'Guernsey and Alderney',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Heard Island and McDonald Islands',
  'Honduras',
  'Hong Kong S.A.R.',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jersey',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kosovo',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Macau S.A.R.',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Man (Isle of)',
  'Marshall Islands',
  'Martinique',
  'Mauritania',
  'Mauritius',
  'Mayotte',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Montserrat',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Caledonia',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'Niue',
  'Norfolk Island',
  'North Korea',
  'North Macedonia',
  'Northern Mariana Islands',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestinian Territory Occupied',
  'Panama',
  'Papua new Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Pitcairn Island',
  'Poland',
  'Portugal',
  'Puerto Rico',
  'Qatar',
  'Reunion',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Helena',
  'Saint Kitts And Nevis',
  'Saint Lucia',
  'Saint Pierre and Miquelon',
  'Saint Vincent And The Grenadines',
  'Saint-Barthelemy',
  'Saint-Martin (French part)',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Sint Maarten (Dutch part)',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Georgia',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Svalbard And Jan Mayen Islands',
  'Swaziland',
  'Sweden',
  'Switzerland',
  'Syria',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'The Bahamas',
  'Togo',
  'Tokelau',
  'Tonga',
  'Trinidad And Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Turks And Caicos Islands',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'United States Minor Outlying Islands',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Vatican City State (Holy See)',
  'Venezuela',
  'Vietnam',
  'Virgin Islands (British)',
  'Virgin Islands (US)',
  'Wallis And Futuna Islands',
  'Western Sahara',
  'Yemen',
  'Zambia',
  'Zimbabwe',
] as const

/**
 * Represents all possible countries & regions with their corresponding English
 * names.
 */
export const EnglishCountryNames: Record<Country, string> = {
  Afghanistan: 'Afghanistan',
  'Aland Islands': 'Aland Islands',
  Albania: 'Albania',
  Algeria: 'Algeria',
  'American Samoa': 'American Samoa',
  Andorra: 'Andorra',
  Angola: 'Angola',
  Anguilla: 'Anguilla',
  Antarctica: 'Antarctica',
  'Antigua And Barbuda': 'Antigua And Barbuda',
  Argentina: 'Argentina',
  Armenia: 'Armenia',
  Aruba: 'Aruba',
  Australia: 'Australia',
  Austria: 'Austria',
  Azerbaijan: 'Azerbaijan',
  Bahrain: 'Bahrain',
  Bangladesh: 'Bangladesh',
  Barbados: 'Barbados',
  Belarus: 'Belarus',
  Belgium: 'Belgium',
  Belize: 'Belize',
  Benin: 'Benin',
  Bermuda: 'Bermuda',
  Bhutan: 'Bhutan',
  Bolivia: 'Bolivia',
  'Bonaire, Sint Eustatius and Saba': 'Bonaire',
  'Bosnia and Herzegovina': 'Bosnia and Herzegovina',
  Botswana: 'Botswana',
  'Bouvet Island': 'Bouvet Island',
  Brazil: 'Brazil',
  'British Indian Ocean Territory': 'British Indian Ocean Territory',
  Brunei: 'Brunei',
  Bulgaria: 'Bulgaria',
  'Burkina Faso': 'Burkina Faso',
  Burundi: 'Burundi',
  Cambodia: 'Cambodia',
  Cameroon: 'Cameroon',
  Canada: 'Canada',
  'Cape Verde': 'Cape Verde',
  'Cayman Islands': 'Cayman Islands',
  'Central African Republic': 'Central African Republic',
  Chad: 'Chad',
  Chile: 'Chile',
  China: 'China',
  'Christmas Island': 'Christmas Island',
  'Cocos (Keeling) Islands': 'Cocos (Keeling) Islands',
  Colombia: 'Colombia',
  Comoros: 'Comoros',
  Congo: 'Congo',
  'Cook Islands': 'Cook Islands',
  'Costa Rica': 'Costa Rica',
  "Cote D'Ivoire (Ivory Coast)": "Cote D'Ivoire (Ivory Coast)",
  Croatia: 'Croatia',
  Cuba: 'Cuba',
  Curaçao: 'Curaçao',
  Cyprus: 'Cyprus',
  'Czech Republic': 'Czech Republic',
  'Democratic Republic of the Congo': 'Democratic Republic of the Congo',
  Denmark: 'Denmark',
  Djibouti: 'Djibouti',
  Dominica: 'Dominica',
  'Dominican Republic': 'Dominican Republic',
  'East Timor': 'East Timor',
  Ecuador: 'Ecuador',
  Egypt: 'Egypt',
  'El Salvador': 'El Salvador',
  'Equatorial Guinea': 'Equatorial Guinea',
  Eritrea: 'Eritrea',
  Estonia: 'Estonia',
  Ethiopia: 'Ethiopia',
  'Falkland Islands': 'Falkland Islands',
  'Faroe Islands': 'Faroe Islands',
  'Fiji Islands': 'Fiji Islands',
  Finland: 'Finland',
  France: 'France',
  'French Guiana': 'French Guiana',
  'French Polynesia': 'French Polynesia',
  'French Southern Territories': 'French Southern Territories',
  Gabon: 'Gabon',
  'Gambia The': 'Gambia The',
  Georgia: 'Georgia',
  Germany: 'Germany',
  Ghana: 'Ghana',
  Gibraltar: 'Gibraltar',
  Greece: 'Greece',
  Greenland: 'Greenland',
  Grenada: 'Grenada',
  Guadeloupe: 'Guadeloupe',
  Guam: 'Guam',
  Guatemala: 'Guatemala',
  'Guernsey and Alderney': 'Guernsey and Alderney',
  Guinea: 'Guinea',
  'Guinea-Bissau': 'Guinea-Bissau',
  Guyana: 'Guyana',
  Haiti: 'Haiti',
  'Heard Island and McDonald Islands': 'Heard Island and McDonald Islands',
  Honduras: 'Honduras',
  'Hong Kong S.A.R.': 'Hong Kong S.A.R.',
  Hungary: 'Hungary',
  Iceland: 'Iceland',
  India: 'India',
  Indonesia: 'Indonesia',
  Iran: 'Iran',
  Iraq: 'Iraq',
  Ireland: 'Ireland',
  Israel: 'Israel',
  Italy: 'Italy',
  Jamaica: 'Jamaica',
  Japan: 'Japan',
  Jersey: 'Jersey',
  Jordan: 'Jordan',
  Kazakhstan: 'Kazakhstan',
  Kenya: 'Kenya',
  Kiribati: 'Kiribati',
  Kosovo: 'Kosovo',
  Kuwait: 'Kuwait',
  Kyrgyzstan: 'Kyrgyzstan',
  Laos: 'Laos',
  Latvia: 'Latvia',
  Lebanon: 'Lebanon',
  Lesotho: 'Lesotho',
  Liberia: 'Liberia',
  Libya: 'Libya',
  Liechtenstein: 'Liechtenstein',
  Lithuania: 'Lithuania',
  Luxembourg: 'Luxembourg',
  'Macau S.A.R.': 'Macau S.A.R.',
  Madagascar: 'Madagascar',
  Malawi: 'Malawi',
  Malaysia: 'Malaysia',
  Maldives: 'Maldives',
  Mali: 'Mali',
  Malta: 'Malta',
  'Man (Isle of)': 'Man (Isle of)',
  'Marshall Islands': 'Marshall Islands',
  Martinique: 'Martinique',
  Mauritania: 'Mauritania',
  Mauritius: 'Mauritius',
  Mayotte: 'Mayotte',
  Mexico: 'Mexico',
  Micronesia: 'Micronesia',
  Moldova: 'Moldova',
  Monaco: 'Monaco',
  Mongolia: 'Mongolia',
  Montenegro: 'Montenegro',
  Montserrat: 'Montserrat',
  Morocco: 'Morocco',
  Mozambique: 'Mozambique',
  Myanmar: 'Myanmar',
  Namibia: 'Namibia',
  Nauru: 'Nauru',
  Nepal: 'Nepal',
  Netherlands: 'Netherlands',
  'New Caledonia': 'New Caledonia',
  'New Zealand': 'New Zealand',
  Nicaragua: 'Nicaragua',
  Niger: 'Niger',
  Nigeria: 'Nigeria',
  Niue: 'Niue',
  'Norfolk Island': 'Norfolk Island',
  'North Korea': 'North Korea',
  'North Macedonia': 'North Macedonia',
  'Northern Mariana Islands': 'Northern Mariana Islands',
  Norway: 'Norway',
  Oman: 'Oman',
  Pakistan: 'Pakistan',
  Palau: 'Palau',
  'Palestinian Territory Occupied': 'Palestinian Territory Occupied',
  Panama: 'Panama',
  'Papua new Guinea': 'Papua new Guinea',
  Paraguay: 'Paraguay',
  Peru: 'Peru',
  Philippines: 'Philippines',
  'Pitcairn Island': 'Pitcairn Island',
  Poland: 'Poland',
  Portugal: 'Portugal',
  'Puerto Rico': 'Puerto Rico',
  Qatar: 'Qatar',
  Reunion: 'Reunion',
  Romania: 'Romania',
  Russia: 'Russia',
  Rwanda: 'Rwanda',
  'Saint Helena': 'Saint Helena',
  'Saint Kitts And Nevis': 'Saint Kitts And Nevis',
  'Saint Lucia': 'Saint Lucia',
  'Saint Pierre and Miquelon': 'Saint Pierre and Miquelon',
  'Saint Vincent And The Grenadines': 'Saint Vincent And The Grenadines',
  'Saint-Barthelemy': 'Saint-Barthelemy',
  'Saint-Martin (French part)': 'Saint-Martin (French part)',
  Samoa: 'Samoa',
  'San Marino': 'San Marino',
  'Sao Tome and Principe': 'Sao Tome and Principe',
  'Saudi Arabia': 'Saudi Arabia',
  Senegal: 'Senegal',
  Serbia: 'Serbia',
  Seychelles: 'Seychelles',
  'Sierra Leone': 'Sierra Leone',
  Singapore: 'Singapore',
  'Sint Maarten (Dutch part)': 'Sint Maarten (Dutch part)',
  Slovakia: 'Slovakia',
  Slovenia: 'Slovenia',
  'Solomon Islands': 'Solomon Islands',
  Somalia: 'Somalia',
  'South Africa': 'South Africa',
  'South Georgia': 'South Georgia',
  'South Korea': 'South Korea',
  'South Sudan': 'South Sudan',
  Spain: 'Spain',
  'Sri Lanka': 'Sri Lanka',
  Sudan: 'Sudan',
  Suriname: 'Suriname',
  'Svalbard And Jan Mayen Islands': 'Svalbard And Jan Mayen Islands',
  Swaziland: 'Swaziland',
  Sweden: 'Sweden',
  Switzerland: 'Switzerland',
  Syria: 'Syria',
  Taiwan: 'Taiwan',
  Tajikistan: 'Tajikistan',
  Tanzania: 'Tanzania',
  Thailand: 'Thailand',
  'The Bahamas': 'The Bahamas',
  Togo: 'Togo',
  Tokelau: 'Tokelau',
  Tonga: 'Tonga',
  'Trinidad And Tobago': 'Trinidad And Tobago',
  Tunisia: 'Tunisia',
  Turkey: 'Turkey',
  Turkmenistan: 'Turkmenistan',
  'Turks And Caicos Islands': 'Turks And Caicos Islands',
  Tuvalu: 'Tuvalu',
  Uganda: 'Uganda',
  Ukraine: 'Ukraine',
  'United Arab Emirates': 'United Arab Emirates',
  'United Kingdom': 'United Kingdom',
  'United States': 'United States',
  'United States Minor Outlying Islands':
    'United States Minor Outlying Islands',
  Uruguay: 'Uruguay',
  Uzbekistan: 'Uzbekistan',
  Vanuatu: 'Vanuatu',
  'Vatican City State (Holy See)': 'Vatican City State (Holy See)',
  Venezuela: 'Venezuela',
  Vietnam: 'Vietnam',
  'Virgin Islands (British)': 'Virgin Islands (British)',
  'Virgin Islands (US)': 'Virgin Islands (US)',
  'Wallis And Futuna Islands': 'Wallis And Futuna Islands',
  'Western Sahara': 'Western Sahara',
  Yemen: 'Yemen',
  Zambia: 'Zambia',
  Zimbabwe: 'Zimbabwe',
}

/**
 * Represents all possible countries & regions with their corresponding
 * Simplified Chinese names.
 */
export const SimplifiedChineseCountryNames: Record<Country, string> = {
  Afghanistan: '阿富汗',
  'Aland Islands': '奥兰群岛',
  Albania: '阿尔巴尼亚',
  Algeria: '阿尔及利亚',
  'American Samoa': '美属萨摩亚',
  Andorra: '安道尔',
  Angola: '安哥拉',
  Anguilla: '安圭拉',
  Antarctica: '南极洲',
  'Antigua And Barbuda': '安提瓜和巴布达',
  Argentina: '阿根廷',
  Armenia: '亚美尼亚',
  Aruba: '阿鲁巴',
  Australia: '澳大利亚',
  Austria: '奥地利',
  Azerbaijan: '阿塞拜疆',
  Bahrain: '巴林',
  Bangladesh: '孟加拉',
  Barbados: '巴巴多斯',
  Belarus: '白俄罗斯',
  Belgium: '比利时',
  Belize: '伯利兹',
  Benin: '贝宁',
  Bermuda: '百慕大',
  Bhutan: '不丹',
  Bolivia: '玻利维亚',
  'Bonaire, Sint Eustatius and Saba': '博内尔岛、圣尤斯特歇斯和萨巴岛',
  'Bosnia and Herzegovina': '波斯尼亚和黑塞哥维那',
  Botswana: '博茨瓦纳',
  'Bouvet Island': '布维岛',
  Brazil: '巴西',
  'British Indian Ocean Territory': '英属印度洋领地',
  Brunei: '文莱',
  Bulgaria: '保加利亚',
  'Burkina Faso': '布基纳法索',
  Burundi: '布隆迪',
  Cambodia: '柬埔寨',
  Cameroon: '喀麦隆',
  Canada: '加拿大',
  'Cape Verde': '佛得角',
  'Cayman Islands': '开曼群岛',
  'Central African Republic': '中非',
  Chad: '乍得',
  Chile: '智利',
  China: '中国',
  'Christmas Island': '圣诞岛',
  'Cocos (Keeling) Islands': '科科斯（基林）群岛',
  Colombia: '哥伦比亚',
  Comoros: '科摩罗',
  Congo: '刚果',
  'Cook Islands': '库克群岛',
  'Costa Rica': '哥斯达黎加',
  "Cote D'Ivoire (Ivory Coast)": '科特迪瓦',
  Croatia: '克罗地亚',
  Cuba: '古巴',
  Curaçao: '库拉索',
  Cyprus: '塞浦路斯',
  'Czech Republic': '捷克',
  'Democratic Republic of the Congo': '刚果（金）',
  Denmark: '丹麦',
  Djibouti: '吉布提',
  Dominica: '多米尼加',
  'Dominican Republic': '多明尼加共和国',
  'East Timor': '东帝汶',
  Ecuador: '厄瓜多尔',
  Egypt: '埃及',
  'El Salvador': '萨尔瓦多',
  'Equatorial Guinea': '赤道几内亚',
  Eritrea: '厄立特里亚',
  Estonia: '爱沙尼亚',
  Ethiopia: '埃塞俄比亚',
  'Falkland Islands': '福克兰群岛',
  'Faroe Islands': '法罗群岛',
  'Fiji Islands': '斐济',
  Finland: '芬兰',
  France: '法国',
  'French Guiana': '法属圭亚那',
  'French Polynesia': '法属波利尼西亚',
  'French Southern Territories': '法属南部领地',
  Gabon: '加蓬',
  'Gambia The': '冈比亚',
  Georgia: '格鲁吉亚',
  Germany: '德国',
  Ghana: '加纳',
  Gibraltar: '直布罗陀',
  Greece: '希腊',
  Greenland: '格陵兰岛',
  Grenada: '格林纳达',
  Guadeloupe: '瓜德罗普岛',
  Guam: '关岛',
  Guatemala: '危地马拉',
  'Guernsey and Alderney': '根西岛',
  Guinea: '几内亚',
  'Guinea-Bissau': '几内亚比绍',
  Guyana: '圭亚那',
  Haiti: '海地',
  'Heard Island and McDonald Islands': '赫德·唐纳岛及麦唐纳岛',
  Honduras: '洪都拉斯',
  'Hong Kong S.A.R.': '中国香港',
  Hungary: '匈牙利',
  Iceland: '冰岛',
  India: '印度',
  Indonesia: '印度尼西亚',
  Iran: '伊朗',
  Iraq: '伊拉克',
  Ireland: '爱尔兰',
  Israel: '以色列',
  Italy: '意大利',
  Jamaica: '牙买加',
  Japan: '日本',
  Jersey: '泽西岛',
  Jordan: '约旦',
  Kazakhstan: '哈萨克斯坦',
  Kenya: '肯尼亚',
  Kiribati: '基里巴斯',
  Kosovo: '科索沃',
  Kuwait: '科威特',
  Kyrgyzstan: '吉尔吉斯斯坦',
  Laos: '寮人民民主共和国',
  Latvia: '拉脱维亚',
  Lebanon: '黎巴嫩',
  Lesotho: '莱索托',
  Liberia: '利比里亚',
  Libya: '利比亚',
  Liechtenstein: '列支敦士登',
  Lithuania: '立陶宛',
  Luxembourg: '卢森堡',
  'Macau S.A.R.': '中国澳门',
  Madagascar: '马达加斯加',
  Malawi: '马拉维',
  Malaysia: '马来西亚',
  Maldives: '马尔代夫',
  Mali: '马里',
  Malta: '马耳他',
  'Man (Isle of)': '马恩岛',
  'Marshall Islands': '马绍尔群岛',
  Martinique: '马提尼克岛',
  Mauritania: '毛里塔尼亚',
  Mauritius: '毛里求斯',
  Mayotte: '马约特',
  Mexico: '墨西哥',
  Micronesia: '密克罗尼西亚',
  Moldova: '摩尔多瓦',
  Monaco: '摩纳哥',
  Mongolia: '蒙古',
  Montenegro: '黑山',
  Montserrat: '蒙特塞拉特',
  Morocco: '摩洛哥',
  Mozambique: '莫桑比克',
  Myanmar: '缅甸',
  Namibia: '纳米比亚',
  Nauru: '瑙鲁',
  Nepal: '尼泊尔',
  Netherlands: '荷兰',
  'New Caledonia': '新喀里多尼亚',
  'New Zealand': '新西兰',
  Nicaragua: '尼加拉瓜',
  Niger: '尼日尔',
  Nigeria: '尼日利亚',
  Niue: '纽埃',
  'Norfolk Island': '诺福克岛',
  'North Korea': '朝鲜',
  'North Macedonia': '北馬其頓',
  'Northern Mariana Islands': '北马里亚纳群岛',
  Norway: '挪威',
  Oman: '阿曼',
  Pakistan: '巴基斯坦',
  Palau: '帕劳',
  'Palestinian Territory Occupied': '巴勒斯坦',
  Panama: '巴拿马',
  'Papua new Guinea': '巴布亚新几内亚',
  Paraguay: '巴拉圭',
  Peru: '秘鲁',
  Philippines: '菲律宾',
  'Pitcairn Island': '皮特凯恩群岛',
  Poland: '波兰',
  Portugal: '葡萄牙',
  'Puerto Rico': '波多黎各',
  Qatar: '卡塔尔',
  Reunion: '留尼汪岛',
  Romania: '罗马尼亚',
  Russia: '俄罗斯联邦',
  Rwanda: '卢旺达',
  'Saint Helena': '圣赫勒拿',
  'Saint Kitts And Nevis': '圣基茨和尼维斯',
  'Saint Lucia': '圣卢西亚',
  'Saint Pierre and Miquelon': '圣皮埃尔和密克隆',
  'Saint Vincent And The Grenadines': '圣文森特和格林纳丁斯',
  'Saint-Barthelemy': '圣巴泰勒米',
  'Saint-Martin (French part)': '密克罗尼西亞',
  Samoa: '萨摩亚',
  'San Marino': '圣马力诺',
  'Sao Tome and Principe': '圣多美和普林西比',
  'Saudi Arabia': '沙特阿拉伯',
  Senegal: '塞内加尔',
  Serbia: '塞尔维亚',
  Seychelles: '塞舌尔',
  'Sierra Leone': '塞拉利昂',
  Singapore: '新加坡',
  'Sint Maarten (Dutch part)': '圣马丁岛（荷兰部分）',
  Slovakia: '斯洛伐克',
  Slovenia: '斯洛文尼亚',
  'Solomon Islands': '所罗门群岛',
  Somalia: '索马里',
  'South Africa': '南非',
  'South Georgia': '南乔治亚',
  'South Korea': '韩国',
  'South Sudan': '南苏丹',
  Spain: '西班牙',
  'Sri Lanka': '斯里兰卡',
  Sudan: '苏丹',
  Suriname: '苏里南',
  'Svalbard And Jan Mayen Islands': '斯瓦尔巴和扬马延群岛',
  Swaziland: '斯威士兰',
  Sweden: '瑞典',
  Switzerland: '瑞士',
  Syria: '叙利亚',
  Taiwan: '中国台湾',
  Tajikistan: '塔吉克斯坦',
  Tanzania: '坦桑尼亚',
  Thailand: '泰国',
  'The Bahamas': '巴哈马',
  Togo: '多哥',
  Tokelau: '托克劳',
  Tonga: '汤加',
  'Trinidad And Tobago': '特立尼达和多巴哥',
  Tunisia: '突尼斯',
  Turkey: '土耳其',
  Turkmenistan: '土库曼斯坦',
  'Turks And Caicos Islands': '特克斯和凯科斯群岛',
  Tuvalu: '图瓦卢',
  Uganda: '乌干达',
  Ukraine: '乌克兰',
  'United Arab Emirates': '阿拉伯联合酋长国',
  'United Kingdom': '英国',
  'United States': '美国',
  'United States Minor Outlying Islands': '美国本土外小岛屿',
  Uruguay: '乌拉圭',
  Uzbekistan: '乌兹别克斯坦',
  Vanuatu: '瓦努阿图',
  'Vatican City State (Holy See)': '梵蒂冈',
  Venezuela: '委内瑞拉',
  Vietnam: '越南',
  'Virgin Islands (British)': '圣文森特和格林纳丁斯',
  'Virgin Islands (US)': '维尔京群岛（美国）',
  'Wallis And Futuna Islands': '瓦利斯群岛和富图纳群岛',
  'Western Sahara': '西撒哈拉',
  Yemen: '也门',
  Zambia: '赞比亚',
  Zimbabwe: '津巴布韦',
}

/**
 * Represents all possible countries & regions with their corresponding
 * Traditional Chinese HK names.
 */
export const TraditionalChineseCountryHKNames: Record<Country, string> = {
  Afghanistan: '阿富汗',
  'Aland Islands': '奧蘭羣島',
  Albania: '阿爾巴尼亞',
  Algeria: '阿爾及利亞',
  'American Samoa': '美屬薩摩亞',
  Andorra: '安道爾',
  Angola: '安哥拉',
  Anguilla: '安圭拉',
  Antarctica: '南極洲',
  'Antigua And Barbuda': '安提瓜和巴布達',
  Argentina: '阿根廷',
  Armenia: '亞美尼亞',
  Aruba: '阿魯巴',
  Australia: '澳大利亞',
  Austria: '奧地利',
  Azerbaijan: '阿塞拜疆',
  Bahrain: '巴林',
  Bangladesh: '孟加拉',
  Barbados: '巴巴多斯',
  Belarus: '白俄羅斯',
  Belgium: '比利時',
  Belize: '伯利茲',
  Benin: '貝寧',
  Bermuda: '百慕大',
  Bhutan: '不丹',
  Bolivia: '玻利維亞',
  'Bonaire, Sint Eustatius and Saba': '博內爾島、聖尤斯特歇斯和薩巴島',
  'Bosnia and Herzegovina': '波斯尼亞和黑塞哥維那',
  Botswana: '博茨瓦納',
  'Bouvet Island': '布維島',
  Brazil: '巴西',
  'British Indian Ocean Territory': '英屬印度洋領地',
  Brunei: '文萊',
  Bulgaria: '保加利亞',
  'Burkina Faso': '布基納法索',
  Burundi: '布隆迪',
  Cambodia: '柬埔寨',
  Cameroon: '喀麥隆',
  Canada: '加拿大',
  'Cape Verde': '佛得角',
  'Cayman Islands': '開曼羣島',
  'Central African Republic': '中非',
  Chad: '乍得',
  Chile: '智利',
  China: '中國',
  'Christmas Island': '聖誕島',
  'Cocos (Keeling) Islands': '科科斯（基林）羣島',
  Colombia: '哥倫比亞',
  Comoros: '科摩羅',
  Congo: '剛果',
  'Cook Islands': '庫克羣島',
  'Costa Rica': '哥斯達黎加',
  "Cote D'Ivoire (Ivory Coast)": '科特迪瓦',
  Croatia: '克羅地亞',
  Cuba: '古巴',
  Curaçao: '庫拉索',
  Cyprus: '塞浦路斯',
  'Czech Republic': '捷克',
  'Democratic Republic of the Congo': '剛果（金）',
  Denmark: '丹麥',
  Djibouti: '吉布提',
  Dominica: '多米尼加',
  'Dominican Republic': '多明尼加共和國',
  'East Timor': '東帝汶',
  Ecuador: '厄瓜多爾',
  Egypt: '埃及',
  'El Salvador': '薩爾瓦多',
  'Equatorial Guinea': '赤道幾內亞',
  Eritrea: '厄立特里亞',
  Estonia: '愛沙尼亞',
  Ethiopia: '埃塞俄比亞',
  'Falkland Islands': '福克蘭羣島',
  'Faroe Islands': '法羅羣島',
  'Fiji Islands': '斐濟',
  Finland: '芬蘭',
  France: '法國',
  'French Guiana': '法屬圭亞那',
  'French Polynesia': '法屬波利尼西亞',
  'French Southern Territories': '法屬南部領地',
  Gabon: '加蓬',
  'Gambia The': '岡比亞',
  Georgia: '格魯吉亞',
  Germany: '德國',
  Ghana: '加納',
  Gibraltar: '直布羅陀',
  Greece: '希臘',
  Greenland: '格陵蘭島',
  Grenada: '格林納達',
  Guadeloupe: '瓜德羅普島',
  Guam: '關島',
  Guatemala: '危地馬拉',
  'Guernsey and Alderney': '根西島',
  Guinea: '幾內亞',
  'Guinea-Bissau': '幾內亞比紹',
  Guyana: '圭亞那',
  Haiti: '海地',
  'Heard Island and McDonald Islands': '赫德·唐納島及麥唐納島',
  Honduras: '洪都拉斯',
  'Hong Kong S.A.R.': '中國香港',
  Hungary: '匈牙利',
  Iceland: '冰島',
  India: '印度',
  Indonesia: '印度尼西亞',
  Iran: '伊朗',
  Iraq: '伊拉克',
  Ireland: '愛爾蘭',
  Israel: '以色列',
  Italy: '意大利',
  Jamaica: '牙買加',
  Japan: '日本',
  Jersey: '澤西島',
  Jordan: '約旦',
  Kazakhstan: '哈薩克斯坦',
  Kenya: '肯尼亞',
  Kiribati: '基里巴斯',
  Kosovo: '科索沃',
  Kuwait: '科威特',
  Kyrgyzstan: '吉爾吉斯斯坦',
  Laos: '寮人民民主共和國',
  Latvia: '拉脫維亞',
  Lebanon: '黎巴嫩',
  Lesotho: '萊索托',
  Liberia: '利比里亞',
  Libya: '利比亞',
  Liechtenstein: '列支敦士登',
  Lithuania: '立陶宛',
  Luxembourg: '盧森堡',
  'Macau S.A.R.': '中國澳門',
  Madagascar: '馬達加斯加',
  Malawi: '馬拉維',
  Malaysia: '馬來西亞',
  Maldives: '馬爾代夫',
  Mali: '馬裏',
  Malta: '馬耳他',
  'Man (Isle of)': '馬恩島',
  'Marshall Islands': '馬紹爾羣島',
  Martinique: '馬提尼克島',
  Mauritania: '毛里塔尼亞',
  Mauritius: '毛里求斯',
  Mayotte: '馬約特',
  Mexico: '墨西哥',
  Micronesia: '密克羅尼西亞',
  Moldova: '摩爾多瓦',
  Monaco: '摩納哥',
  Mongolia: '蒙古',
  Montenegro: '黑山',
  Montserrat: '蒙特塞拉特',
  Morocco: '摩洛哥',
  Mozambique: '莫桑比克',
  Myanmar: '緬甸',
  Namibia: '納米比亞',
  Nauru: '瑙魯',
  Nepal: '尼泊爾',
  Netherlands: '荷蘭',
  'New Caledonia': '新喀里多尼亞',
  'New Zealand': '新西蘭',
  Nicaragua: '尼加拉瓜',
  Niger: '尼日爾',
  Nigeria: '尼日利亞',
  Niue: '紐埃',
  'Norfolk Island': '諾福克島',
  'North Korea': '朝鮮',
  'North Macedonia': '北馬其頓',
  'Northern Mariana Islands': '北马里亞納羣島',
  Norway: '挪威',
  Oman: '阿曼',
  Pakistan: '巴基斯坦',
  Palau: '帕勞',
  'Palestinian Territory Occupied': '巴勒斯坦',
  Panama: '巴拿馬',
  'Papua new Guinea': '巴布亞新幾內亞',
  Paraguay: '巴拉圭',
  Peru: '祕魯',
  Philippines: '菲律賓',
  'Pitcairn Island': '皮特凱恩羣島',
  Poland: '波蘭',
  Portugal: '葡萄牙',
  'Puerto Rico': '波多黎各',
  Qatar: '卡塔爾',
  Reunion: '留尼汪島',
  Romania: '羅馬尼亞',
  Russia: '俄羅斯聯邦',
  Rwanda: '盧旺達',
  'Saint Helena': '聖赫勒拿',
  'Saint Kitts And Nevis': '聖基茨和尼維斯',
  'Saint Lucia': '聖盧西亞',
  'Saint Pierre and Miquelon': '聖皮埃爾和密克隆',
  'Saint Vincent And The Grenadines': '聖文森特和格林納丁斯',
  'Saint-Barthelemy': '聖巴泰勒米',
  'Saint-Martin (French part)': '密克羅尼西亞',
  Samoa: '薩摩亞',
  'San Marino': '聖馬力諾',
  'Sao Tome and Principe': '聖多美和普林西比',
  'Saudi Arabia': '沙特阿拉伯',
  Senegal: '塞內加爾',
  Serbia: '塞爾維亞',
  Seychelles: '塞舌爾',
  'Sierra Leone': '塞拉利昂',
  Singapore: '新加坡',
  'Sint Maarten (Dutch part)': '聖馬丁島（荷蘭部分）',
  Slovakia: '斯洛伐克',
  Slovenia: '斯洛文尼亞',
  'Solomon Islands': '所羅門羣島',
  Somalia: '索馬里',
  'South Africa': '南非',
  'South Georgia': '南喬治亞',
  'South Korea': '韓國',
  'South Sudan': '南蘇丹',
  Spain: '西班牙',
  'Sri Lanka': '斯里蘭卡',
  Sudan: '蘇丹',
  Suriname: '蘇利南',
  'Svalbard And Jan Mayen Islands': '斯瓦爾巴和揚馬延羣島',
  Swaziland: '斯威士蘭',
  Sweden: '瑞典',
  Switzerland: '瑞士',
  Syria: '敘利亞',
  Taiwan: '中國臺灣',
  Tajikistan: '塔吉克斯坦',
  Tanzania: '坦桑尼亞',
  Thailand: '泰國',
  'The Bahamas': '巴哈馬',
  Togo: '多哥',
  Tokelau: '托克勞',
  Tonga: '湯加',
  'Trinidad And Tobago': '特立尼達和多巴哥',
  Tunisia: '突尼斯',
  Turkey: '土耳其',
  Turkmenistan: '土庫曼斯坦',
  'Turks And Caicos Islands': '特克斯和凱科斯羣島',
  Tuvalu: '圖瓦盧',
  Uganda: '烏干達',
  Ukraine: '烏克蘭',
  'United Arab Emirates': '阿拉伯聯合酋長國',
  'United Kingdom': '英國',
  'United States': '美國',
  'United States Minor Outlying Islands': '美國本土外小島嶼',
  Uruguay: '烏拉圭',
  Uzbekistan: '烏茲別克斯坦',
  Vanuatu: '瓦努阿圖',
  'Vatican City State (Holy See)': '梵蒂岡',
  Venezuela: '委內瑞拉',
  Vietnam: '越南',
  'Virgin Islands (British)': '聖文森特和格林納丁斯',
  'Virgin Islands (US)': '維爾京羣島（美國）',
  'Wallis And Futuna Islands': '瓦利斯羣島和富圖納羣島',
  'Western Sahara': '西撒哈拉',
  Yemen: '也門',
  Zambia: '贊比亞',
  Zimbabwe: '津巴布韋',
}

/**
 * Represents all possible countries & regions with their corresponding
 * Traditional Chinese TW names.
 */
export const TraditionalChineseCountryTWNames: Record<Country, string> = {
  Afghanistan: '阿富汗',
  'Aland Islands': '奧蘭群島',
  Albania: '阿爾巴尼亞',
  Algeria: '阿爾及利亞',
  'American Samoa': '美屬薩摩亞',
  Andorra: '安道爾',
  Angola: '安哥拉',
  Anguilla: '安圭拉',
  Antarctica: '南極洲',
  'Antigua And Barbuda': '安地卡及巴布達',
  Argentina: '阿根廷',
  Armenia: '亞美尼亞',
  Aruba: '阿魯巴',
  Australia: '澳大利亞',
  Austria: '奧地利',
  Azerbaijan: '亞塞拜然',
  Bahrain: '巴林',
  Bangladesh: '孟加拉',
  Barbados: '巴貝多',
  Belarus: '白俄羅斯',
  Belgium: '比利時',
  Belize: '貝里斯',
  Benin: '貝南',
  Bermuda: '百慕大',
  Bhutan: '不丹',
  Bolivia: '玻利維亞',
  'Bonaire, Sint Eustatius and Saba': '博內爾島、聖尤斯特歇斯和薩巴島',
  'Bosnia and Herzegovina': '波斯尼亞和黑塞哥維那',
  Botswana: '波札那',
  'Bouvet Island': '布維島',
  Brazil: '巴西',
  'British Indian Ocean Territory': '英屬印度洋領地',
  Brunei: '汶萊',
  Bulgaria: '保加利亞',
  'Burkina Faso': '布吉納法索',
  Burundi: '蒲隆地',
  Cambodia: '柬埔寨',
  Cameroon: '喀麥隆',
  Canada: '加拿大',
  'Cape Verde': '維德角',
  'Cayman Islands': '開曼群島',
  'Central African Republic': '中非',
  Chad: '查德',
  Chile: '智利',
  China: '中國',
  'Christmas Island': '聖誕島',
  'Cocos (Keeling) Islands': '科科斯（基林）群島',
  Colombia: '哥倫比亞',
  Comoros: '葛摩',
  Congo: '剛果',
  'Cook Islands': '庫克群島',
  'Costa Rica': '哥斯大黎加',
  "Cote D'Ivoire (Ivory Coast)": '象牙海岸',
  Croatia: '克羅埃西亞',
  Cuba: '古巴',
  Curaçao: '庫拉索',
  Cyprus: '塞普勒斯',
  'Czech Republic': '捷克',
  'Democratic Republic of the Congo': '剛果（金）',
  Denmark: '丹麥',
  Djibouti: '吉布提',
  Dominica: '多明尼加',
  'Dominican Republic': '多明尼加共和國',
  'East Timor': '東帝汶',
  Ecuador: '厄瓜多',
  Egypt: '埃及',
  'El Salvador': '薩爾瓦多',
  'Equatorial Guinea': '赤道幾內亞',
  Eritrea: '厄利垂亞',
  Estonia: '愛沙尼亞',
  Ethiopia: '衣索比亞',
  'Falkland Islands': '福克蘭羣島',
  'Faroe Islands': '法羅群島',
  'Fiji Islands': '斐濟',
  Finland: '芬蘭',
  France: '法國',
  'French Guiana': '法屬蓋亞那',
  'French Polynesia': '法屬波利尼西亞',
  'French Southern Territories': '法屬南部領地',
  Gabon: '加彭',
  'Gambia The': '甘比亞',
  Georgia: '喬治亞',
  Germany: '德國',
  Ghana: '迦納',
  Gibraltar: '直布羅陀',
  Greece: '希臘',
  Greenland: '格陵蘭島',
  Grenada: '格瑞那達',
  Guadeloupe: '瓜德羅普島',
  Guam: '關島',
  Guatemala: '瓜地馬拉',
  'Guernsey and Alderney': '根西島',
  Guinea: '幾內亞',
  'Guinea-Bissau': '幾內亞比索',
  Guyana: '蓋亞那',
  Haiti: '海地',
  'Heard Island and McDonald Islands': '赫德·唐納島及麥唐納島',
  Honduras: '宏都拉斯',
  'Hong Kong S.A.R.': '中國香港',
  Hungary: '匈牙利',
  Iceland: '冰島',
  India: '印度',
  Indonesia: '印度尼西亞',
  Iran: '伊朗',
  Iraq: '伊拉克',
  Ireland: '愛爾蘭',
  Israel: '以色列',
  Italy: '義大利',
  Jamaica: '牙買加',
  Japan: '日本',
  Jersey: '澤西島',
  Jordan: '約旦',
  Kazakhstan: '哈薩克',
  Kenya: '肯亞',
  Kiribati: '吉里巴斯',
  Kosovo: '科索沃',
  Kuwait: '科威特',
  Kyrgyzstan: '吉爾吉斯斯坦',
  Laos: '寮人民民主共和國',
  Latvia: '拉脫維亞',
  Lebanon: '黎巴嫩',
  Lesotho: '賴索托',
  Liberia: '賴比瑞亞',
  Libya: '利比亞',
  Liechtenstein: '列支敦斯登',
  Lithuania: '立陶宛',
  Luxembourg: '盧森堡',
  'Macau S.A.R.': '中國澳門',
  Madagascar: '馬達加斯加',
  Malawi: '馬拉維',
  Malaysia: '馬來西亞',
  Maldives: '馬爾地夫',
  Mali: '馬裡',
  Malta: '馬耳他',
  'Man (Isle of)': '馬恩島',
  'Marshall Islands': '馬紹爾羣島',
  Martinique: '馬提尼克島',
  Mauritania: '茅利塔尼亞',
  Mauritius: '模里西斯',
  Mayotte: '馬約特',
  Mexico: '墨西哥',
  Micronesia: '密克羅尼西亞',
  Moldova: '摩爾多瓦',
  Monaco: '摩納哥',
  Mongolia: '蒙古',
  Montenegro: '黑山',
  Montserrat: '蒙特塞拉特',
  Morocco: '摩洛哥',
  Mozambique: '莫三比克',
  Myanmar: '緬甸',
  Namibia: '奈米比亞',
  Nauru: '諾魯',
  Nepal: '尼泊爾',
  Netherlands: '荷蘭',
  'New Caledonia': '新喀里多尼亞',
  'New Zealand': '紐西蘭',
  Nicaragua: '尼加拉瓜',
  Niger: '尼日',
  Nigeria: '奈及利亞',
  Niue: '紐埃',
  'Norfolk Island': '諾福克島',
  'North Korea': '朝鮮',
  'North Macedonia': '北馬其頓',
  'Northern Mariana Islands': '北马里亞納羣島',
  Norway: '挪威',
  Oman: '阿曼',
  Pakistan: '巴基斯坦',
  Palau: '帛琉',
  'Palestinian Territory Occupied': '巴勒斯坦',
  Panama: '巴拿馬',
  'Papua new Guinea': '巴布亞紐幾內亞',
  Paraguay: '巴拉圭',
  Peru: '秘魯',
  Philippines: '菲律賓',
  'Pitcairn Island': '皮特凱恩羣島',
  Poland: '波蘭',
  Portugal: '葡萄牙',
  'Puerto Rico': '波多黎各',
  Qatar: '卡達',
  Reunion: '留尼汪島',
  Romania: '羅馬尼亞',
  Russia: '俄羅斯聯邦',
  Rwanda: '盧安達',
  'Saint Helena': '聖赫勒拿',
  'Saint Kitts And Nevis': '聖克里斯多福及尼維斯',
  'Saint Lucia': '聖露西亞',
  'Saint Pierre and Miquelon': '聖皮埃爾和密克隆',
  'Saint Vincent And The Grenadines': '聖文森及格瑞那丁',
  'Saint-Barthelemy': '聖巴泰勒米',
  'Saint-Martin (French part)': '密克羅尼西亞',
  Samoa: '薩摩亞',
  'San Marino': '聖馬力諾',
  'Sao Tome and Principe': '聖多美和普林西比',
  'Saudi Arabia': '沙烏地阿拉伯',
  Senegal: '塞內加爾',
  Serbia: '塞爾維亞',
  Seychelles: '塞舌爾',
  'Sierra Leone': '獅子山',
  Singapore: '新加坡',
  'Sint Maarten (Dutch part)': '聖馬丁島（荷蘭部分）',
  Slovakia: '斯洛伐克',
  Slovenia: '斯洛維尼亞',
  'Solomon Islands': '索羅門羣島',
  Somalia: '索馬利亞',
  'South Africa': '南非',
  'South Georgia': '南喬治亞',
  'South Korea': '韓國',
  'South Sudan': '南蘇丹',
  Spain: '西班牙',
  'Sri Lanka': '斯里蘭卡',
  Sudan: '蘇丹',
  Suriname: '蘇利南',
  'Svalbard And Jan Mayen Islands': '斯瓦爾巴和揚馬延羣島',
  Swaziland: '史瓦濟蘭',
  Sweden: '瑞典',
  Switzerland: '瑞士',
  Syria: '敘利亞',
  Taiwan: '中國臺灣',
  Tajikistan: '塔吉克',
  Tanzania: '坦尚尼亞',
  Thailand: '泰國',
  'The Bahamas': '巴哈馬',
  Togo: '多哥',
  Tokelau: '托克勞',
  Tonga: '湯加',
  'Trinidad And Tobago': '千里達及托巴哥',
  Tunisia: '突尼西亞',
  Turkey: '土耳其',
  Turkmenistan: '土庫曼',
  'Turks And Caicos Islands': '特克斯和凱科斯羣島',
  Tuvalu: '吐瓦魯',
  Uganda: '烏干達',
  Ukraine: '烏克蘭',
  'United Arab Emirates': '阿拉伯聯合大公國',
  'United Kingdom': '英國',
  'United States': '美國',
  'United States Minor Outlying Islands': '美國本土外小島嶼',
  Uruguay: '烏拉圭',
  Uzbekistan: '烏茲別克',
  Vanuatu: '萬那杜',
  'Vatican City State (Holy See)': '梵蒂岡',
  Venezuela: '委內瑞拉',
  Vietnam: '越南',
  'Virgin Islands (British)': '聖文森及格瑞那丁',
  'Virgin Islands (US)': '維爾京羣島（美國）',
  'Wallis And Futuna Islands': '瓦利斯羣島和富圖納羣島',
  'Western Sahara': '西撒哈拉',
  Yemen: '葉門',
  Zambia: '尚比亞',
  Zimbabwe: '辛巴威',
}

/**
 * Represents all possible countries & regions with their corresponding
 * Spanish names.
 */
export const SpanishCountryNames: Record<Country, string> = {
  Afghanistan: 'Afganistán',
  'Aland Islands': 'Islas Åland',
  Albania: 'Albania',
  Algeria: 'Argelia',
  'American Samoa': 'Samoa Americana',
  Andorra: 'Andorra',
  Angola: 'Angola',
  Anguilla: 'Anguila',
  Antarctica: 'Antártida',
  'Antigua And Barbuda': 'Antigua y Barbuda',
  Argentina: 'Argentina',
  Armenia: 'Armenia',
  Aruba: 'Aruba',
  Australia: 'Australia',
  Austria: 'Austria',
  Azerbaijan: 'Azerbaiyán',
  Bahrain: 'Baréin',
  Bangladesh: 'Bangladés',
  Barbados: 'Barbados',
  Belarus: 'Bielorrusia',
  Belgium: 'Bélgica',
  Belize: 'Belice',
  Benin: 'Benín',
  Bermuda: 'Bermudas',
  Bhutan: 'Bután',
  Bolivia: 'Bolivia',
  'Bonaire, Sint Eustatius and Saba': 'Bonaire, San Eustaquio y Saba',
  'Bosnia and Herzegovina': 'Bosnia y Herzegovina',
  Botswana: 'Botsuana',
  'Bouvet Island': 'Isla Bouvet',
  Brazil: 'Brasil',
  'British Indian Ocean Territory': 'Territorio Británico del Océano Índico',
  Brunei: 'Brunéi',
  Bulgaria: 'Bulgaria',
  'Burkina Faso': 'Burkina Faso',
  Burundi: 'Burundi',
  Cambodia: 'Camboya',
  Cameroon: 'Camerún',
  Canada: 'Canadá',
  'Cape Verde': 'Cabo Verde',
  'Cayman Islands': 'Islas Caimán',
  'Central African Republic': 'República Centroafricana',
  Chad: 'Chad',
  Chile: 'Chile',
  China: 'China',
  'Christmas Island': 'Isla de Navidad',
  'Cocos (Keeling) Islands': 'Islas Cocos',
  Colombia: 'Colombia',
  Comoros: 'Comoras',
  Congo: 'Congo',
  'Cook Islands': 'Islas Cook',
  'Costa Rica': 'Costa Rica',
  "Cote D'Ivoire (Ivory Coast)": 'Costa de Marfil',
  Croatia: 'Croacia',
  Cuba: 'Cuba',
  Curaçao: 'Curazao',
  Cyprus: 'Chipre',
  'Czech Republic': 'República Checa',
  'Democratic Republic of the Congo': 'República Democrática del Congo',
  Denmark: 'Dinamarca',
  Djibouti: 'Yibuti',
  Dominica: 'Dominica',
  'Dominican Republic': 'República Dominicana',
  'East Timor': 'Timor Oriental',
  Ecuador: 'Ecuador',
  Egypt: 'Egipto',
  'El Salvador': 'El Salvador',
  'Equatorial Guinea': 'Guinea Ecuatorial',
  Eritrea: 'Eritrea',
  Estonia: 'Estonia',
  Ethiopia: 'Etiopía',
  'Falkland Islands': 'Islas Malvinas',
  'Faroe Islands': 'Islas Feroe',
  'Fiji Islands': 'Fiyi',
  Finland: 'Finlandia',
  France: 'Francia',
  'French Guiana': 'Guayana Francesa',
  'French Polynesia': 'Polinesia Francesa',
  'French Southern Territories': 'Tierras Australes y Antárticas Francesas',
  Gabon: 'Gabón',
  'Gambia The': 'Gambia',
  Georgia: 'Georgia',
  Germany: 'Alemania',
  Ghana: 'Ghana',
  Gibraltar: 'Gibraltar',
  Greece: 'Grecia',
  Greenland: 'Groenlandia',
  Grenada: 'Granada',
  Guadeloupe: 'Guadalupe',
  Guam: 'Guam',
  Guatemala: 'Guatemala',
  'Guernsey and Alderney': 'Guernsey y Alderney',
  Guinea: 'Guinea',
  'Guinea-Bissau': 'Guinea-Bisáu',
  Guyana: 'Guyana',
  Haiti: 'Haití',
  'Heard Island and McDonald Islands': 'Islas Heard y McDonald',
  Honduras: 'Honduras',
  'Hong Kong S.A.R.': 'Hong Kong',
  Hungary: 'Hungría',
  Iceland: 'Islandia',
  India: 'India',
  Indonesia: 'Indonesia',
  Iran: 'Irán',
  Iraq: 'Irak',
  Ireland: 'Irlanda',
  Israel: 'Israel',
  Italy: 'Italia',
  Jamaica: 'Jamaica',
  Japan: 'Japón',
  Jersey: 'Jersey',
  Jordan: 'Jordania',
  Kazakhstan: 'Kazajistán',
  Kenya: 'Kenia',
  Kiribati: 'Kiribati',
  Kosovo: 'Kosovo',
  Kuwait: 'Kuwait',
  Kyrgyzstan: 'Kirguistán',
  Laos: 'Laos',
  Latvia: 'Letonia',
  Lebanon: 'Líbano',
  Lesotho: 'Lesoto',
  Liberia: 'Liberia',
  Libya: 'Libia',
  Liechtenstein: 'Liechtenstein',
  Lithuania: 'Lituania',
  Luxembourg: 'Luxemburgo',
  'Macau S.A.R.': 'Macao',
  Madagascar: 'Madagascar',
  Malawi: 'Malaui',
  Malaysia: 'Malasia',
  Maldives: 'Maldivas',
  Mali: 'Malí',
  Malta: 'Malta',
  'Man (Isle of)': 'Isla de Man',
  'Marshall Islands': 'Islas Marshall',
  Martinique: 'Martinica',
  Mauritania: 'Mauritania',
  Mauritius: 'Mauricio',
  Mayotte: 'Mayotte',
  Mexico: 'México',
  Micronesia: 'Micronesia',
  Moldova: 'Moldavia',
  Monaco: 'Mónaco',
  Mongolia: 'Mongolia',
  Montenegro: 'Montenegro',
  Montserrat: 'Montserrat',
  Morocco: 'Marruecos',
  Mozambique: 'Mozambique',
  Myanmar: 'Myanmar',
  Namibia: 'Namibia',
  Nauru: 'Nauru',
  Nepal: 'Nepal',
  Netherlands: 'Países Bajos',
  'New Caledonia': 'Nueva Caledonia',
  'New Zealand': 'Nueva Zelanda',
  Nicaragua: 'Nicaragua',
  Niger: 'Níger',
  Nigeria: 'Nigeria',
  Niue: 'Niue',
  'Norfolk Island': 'Isla Norfolk',
  'North Korea': 'Corea del Norte',
  'North Macedonia': 'Macedonia del Norte',
  'Northern Mariana Islands': 'Islas Marianas del Norte',
  Norway: 'Noruega',
  Oman: 'Omán',
  Pakistan: 'Pakistán',
  Palau: 'Palaos',
  'Palestinian Territory Occupied': 'Territorio Palestino Ocupado',
  Panama: 'Panamá',
  'Papua new Guinea': 'Papúa Nueva Guinea',
  Paraguay: 'Paraguay',
  Peru: 'Perú',
  Philippines: 'Filipinas',
  'Pitcairn Island': 'Islas Pitcairn',
  Poland: 'Polonia',
  Portugal: 'Portugal',
  'Puerto Rico': 'Puerto Rico',
  Qatar: 'Catar',
  Reunion: 'Reunión',
  Romania: 'Rumania',
  Russia: 'Rusia',
  Rwanda: 'Ruanda',
  'Saint Helena': 'Santa Elena',
  'Saint Kitts And Nevis': 'San Cristóbal y Nieves',
  'Saint Lucia': 'Santa Lucía',
  'Saint Pierre and Miquelon': 'San Pedro y Miquelón',
  'Saint Vincent And The Grenadines': 'San Vicente y las Granadinas',
  'Saint-Barthelemy': 'San Bartolomé',
  'Saint-Martin (French part)': 'San Martín (parte francesa)',
  Samoa: 'Samoa',
  'San Marino': 'San Marino',
  'Sao Tome and Principe': 'Santo Tomé y Príncipe',
  'Saudi Arabia': 'Arabia Saudita',
  Senegal: 'Senegal',
  Serbia: 'Serbia',
  Seychelles: 'Seychelles',
  'Sierra Leone': 'Sierra Leona',
  Singapore: 'Singapur',
  'Sint Maarten (Dutch part)': 'San Martín (parte neerlandesa)',
  Slovakia: 'Eslovaquia',
  Slovenia: 'Eslovenia',
  'Solomon Islands': 'Islas Salomón',
  Somalia: 'Somalia',
  'South Africa': 'Sudáfrica',
  'South Georgia': 'Georgia del Sur',
  'South Korea': 'Corea del Sur',
  'South Sudan': 'Sudán del Sur',
  Spain: 'España',
  'Sri Lanka': 'Sri Lanka',
  Sudan: 'Sudán',
  Suriname: 'Surinam',
  'Svalbard And Jan Mayen Islands': 'Svalbard y Jan Mayen',
  Swaziland: 'Suazilandia',
  Sweden: 'Suecia',
  Switzerland: 'Suiza',
  Syria: 'Siria',
  Taiwan: 'Taiwán',
  Tajikistan: 'Tayikistán',
  Tanzania: 'Tanzania',
  Thailand: 'Tailandia',
  'The Bahamas': 'Bahamas',
  Togo: 'Togo',
  Tokelau: 'Tokelau',
  Tonga: 'Tonga',
  'Trinidad And Tobago': 'Trinidad y Tobago',
  Tunisia: 'Túnez',
  Turkey: 'Turquía',
  Turkmenistan: 'Turkmenistán',
  'Turks And Caicos Islands': 'Islas Turcas y Caicos',
  Tuvalu: 'Tuvalu',
  Uganda: 'Uganda',
  Ukraine: 'Ucrania',
  'United Arab Emirates': 'Emiratos Árabes Unidos',
  'United Kingdom': 'Reino Unido',
  'United States': 'Estados Unidos',
  'United States Minor Outlying Islands':
    'Islas Ultramarinas Menores de Estados Unidos',
  Uruguay: 'Uruguay',
  Uzbekistan: 'Uzbekistán',
  Vanuatu: 'Vanuatu',
  'Vatican City State (Holy See)': 'Ciudad del Vaticano',
  Venezuela: 'Venezuela',
  Vietnam: 'Vietnam',
  'Virgin Islands (British)': 'Islas Vírgenes Británicas',
  'Virgin Islands (US)': 'Islas Vírgenes de los Estados Unidos',
  'Wallis And Futuna Islands': 'Wallis y Futuna',
  'Western Sahara': 'Sahara Occidental',
  Yemen: 'Yemen',
  Zambia: 'Zambia',
  Zimbabwe: 'Zimbabue',
}

export const NorwegianCountryNames: Record<Country, string> = {
  Afghanistan: 'Afghanistan',
  'Aland Islands': 'Åland',
  Albania: 'Albania',
  Algeria: 'Algerie',
  'American Samoa': 'Amerikansk Samoa',
  Andorra: 'Andorra',
  Angola: 'Angola',
  Anguilla: 'Anguilla',
  Antarctica: 'Antarktis',
  'Antigua And Barbuda': 'Antigua og Barbuda',
  Argentina: 'Argentina',
  Armenia: 'Armenia',
  Aruba: 'Aruba',
  Australia: 'Australia',
  Austria: 'Østerrike',
  Azerbaijan: 'Aserbajdsjan',
  Bahrain: 'Bahrain',
  Bangladesh: 'Bangladesh',
  Barbados: 'Barbados',
  Belarus: 'Belarus',
  Belgium: 'Belgia',
  Belize: 'Belize',
  Benin: 'Benin',
  Bermuda: 'Bermuda',
  Bhutan: 'Bhutan',
  Bolivia: 'Bolivia',
  'Bonaire, Sint Eustatius and Saba': 'Bonaire, Sint Eustatius og Saba',
  'Bosnia and Herzegovina': 'Bosnia-Hercegovina',
  Botswana: 'Botswana',
  'Bouvet Island': 'Bouvetøya',
  Brazil: 'Brasil',
  'British Indian Ocean Territory': 'Det britiske territoriet i Indiahavet',
  Brunei: 'Brunei',
  Bulgaria: 'Bulgaria',
  'Burkina Faso': 'Burkina Faso',
  Burundi: 'Burundi',
  Cambodia: 'Kambodsja',
  Cameroon: 'Kamerun',
  Canada: 'Canada',
  'Cape Verde': 'Kapp Verde',
  'Cayman Islands': 'Caymanøyene',
  'Central African Republic': 'Den sentralafrikanske republikk',
  Chad: 'Tsjad',
  Chile: 'Chile',
  China: 'Kina',
  'Christmas Island': 'Christmasøya',
  'Cocos (Keeling) Islands': 'Kokosøyene (Keelingøyene)',
  Colombia: 'Colombia',
  Comoros: 'Komorene',
  Congo: 'Kongo',
  'Cook Islands': 'Cookøyene',
  'Costa Rica': 'Costa Rica',
  "Cote D'Ivoire (Ivory Coast)": 'Elfenbenskysten',
  Croatia: 'Kroatia',
  Cuba: 'Cuba',
  Curaçao: 'Curaçao',
  Cyprus: 'Kypros',
  'Czech Republic': 'Tsjekkia',
  'Democratic Republic of the Congo': 'Den demokratiske republikken Kongo',
  Denmark: 'Danmark',
  Djibouti: 'Djibouti',
  Dominica: 'Dominica',
  'Dominican Republic': 'Den dominikanske republikk',
  'East Timor': 'Øst-Timor',
  Ecuador: 'Ecuador',
  Egypt: 'Egypt',
  'El Salvador': 'El Salvador',
  'Equatorial Guinea': 'Ekvatorial-Guinea',
  Eritrea: 'Eritrea',
  Estonia: 'Estland',
  Ethiopia: 'Etiopia',
  'Falkland Islands': 'Falklandsøyene',
  'Faroe Islands': 'Færøyene',
  'Fiji Islands': 'Fijiøyene',
  Finland: 'Finland',
  France: 'Frankrike',
  'French Guiana': 'Fransk Guyana',
  'French Polynesia': 'Fransk Polynesia',
  'French Southern Territories': 'Franske sørlige territorier',
  Gabon: 'Gabon',
  'Gambia The': 'Gambia',
  Georgia: 'Georgia',
  Germany: 'Tyskland',
  Ghana: 'Ghana',
  Gibraltar: 'Gibraltar',
  Greece: 'Hellas',
  Greenland: 'Grønland',
  Grenada: 'Grenada',
  Guadeloupe: 'Guadeloupe',
  Guam: 'Guam',
  Guatemala: 'Guatemala',
  'Guernsey and Alderney': 'Guernsey og Alderney',
  Guinea: 'Guinea',
  'Guinea-Bissau': 'Guinea-Bissau',
  Guyana: 'Guyana',
  Haiti: 'Haiti',
  'Heard Island and McDonald Islands': 'Heard- og McDonaldøyene',
  Honduras: 'Honduras',
  'Hong Kong S.A.R.': 'Hong Kong S.A.R.',
  Hungary: 'Ungarn',
  Iceland: 'Island',
  India: 'India',
  Indonesia: 'Indonesia',
  Iran: 'Iran',
  Iraq: 'Irak',
  Ireland: 'Irland',
  Israel: 'Israel',
  Italy: 'Italia',
  Jamaica: 'Jamaica',
  Japan: 'Japan',
  Jersey: 'Jersey',
  Jordan: 'Jordan',
  Kazakhstan: 'Kasakhstan',
  Kenya: 'Kenya',
  Kiribati: 'Kiribati',
  Kosovo: 'Kosovo',
  Kuwait: 'Kuwait',
  Kyrgyzstan: 'Kirgisistan',
  Laos: 'Laos',
  Latvia: 'Latvia',
  Lebanon: 'Libanon',
  Lesotho: 'Lesotho',
  Liberia: 'Liberia',
  Libya: 'Libya',
  Liechtenstein: 'Liechtenstein',
  Lithuania: 'Litauen',
  Luxembourg: 'Luxembourg',
  'Macau S.A.R.': 'Macau S.A.R.',
  Madagascar: 'Madagaskar',
  Malawi: 'Malawi',
  Malaysia: 'Malaysia',
  Maldives: 'Maldivene',
  Mali: 'Mali',
  Malta: 'Malta',
  'Man (Isle of)': 'Man (Isle of)',
  'Marshall Islands': 'Marshalløyene',
  Martinique: 'Martinique',
  Mauritania: 'Mauritania',
  Mauritius: 'Mauritius',
  Mayotte: 'Mayotte',
  Mexico: 'Mexico',
  Micronesia: 'Mikronesia',
  Moldova: 'Moldova',
  Monaco: 'Monaco',
  Mongolia: 'Mongolia',
  Montenegro: 'Montenegro',
  Montserrat: 'Montserrat',
  Morocco: 'Marokko',
  Mozambique: 'Mosambik',
  Myanmar: 'Myanmar',
  Namibia: 'Namibia',
  Nauru: 'Nauru',
  Nepal: 'Nepal',
  Netherlands: 'Nederland',
  'New Caledonia': 'Ny-Caledonia',
  'New Zealand': 'New Zealand',
  Nicaragua: 'Nicaragua',
  Niger: 'Niger',
  Nigeria: 'Nigeria',
  Niue: 'Niue',
  'Norfolk Island': 'Norfolkøya',
  'North Korea': 'Nord-Korea',
  'North Macedonia': 'Nord-Makedonia',
  'Northern Mariana Islands': 'Nord-Marianene',
  Norway: 'Norge',
  Oman: 'Oman',
  Pakistan: 'Pakistan',
  Palau: 'Palau',
  'Palestinian Territory Occupied': 'Palestinsk okkupert område',
  Panama: 'Panama',
  'Papua new Guinea': 'Papua Ny-Guinea',
  Paraguay: 'Paraguay',
  Peru: 'Peru',
  Philippines: 'Filippinene',
  'Pitcairn Island': 'Pitcairnøyene',
  Poland: 'Polen',
  Portugal: 'Portugal',
  'Puerto Rico': 'Puerto Rico',
  Qatar: 'Qatar',
  Reunion: 'Réunion',
  Romania: 'Romania',
  Russia: 'Russland',
  Rwanda: 'Rwanda',
  'Saint Helena': 'Sankt Helena',
  'Saint Kitts And Nevis': 'Saint Kitts og Nevis',
  'Saint Lucia': 'Saint Lucia',
  'Saint Pierre and Miquelon': 'Saint-Pierre og Miquelon',
  'Saint Vincent And The Grenadines': 'Saint Vincent og Grenadinene',
  'Saint-Barthelemy': 'Saint-Barthélemy',
  'Saint-Martin (French part)': 'Saint-Martin (fransk del)',
  Samoa: 'Samoa',
  'San Marino': 'San Marino',
  'Sao Tome and Principe': 'São Tomé og Príncipe',
  'Saudi Arabia': 'Saudi-Arabia',
  Senegal: 'Senegal',
  Serbia: 'Serbia',
  Seychelles: 'Seychellene',
  'Sierra Leone': 'Sierra Leone',
  Singapore: 'Singapore',
  'Sint Maarten (Dutch part)': 'Sint Maarten (nederlandsk del)',
  Slovakia: 'Slovakia',
  Slovenia: 'Slovenia',
  'Solomon Islands': 'Salomonøyene',
  Somalia: 'Somalia',
  'South Africa': 'Sør-Afrika',
  'South Georgia': 'Sør-Georgia',
  'South Korea': 'Sør-Korea',
  'South Sudan': 'Sør-Sudan',
  Spain: 'Spania',
  'Sri Lanka': 'Sri Lanka',
  Sudan: 'Sudan',
  Suriname: 'Surinam',
  'Svalbard And Jan Mayen Islands': 'Svalbard og Jan Mayen',
  Swaziland: 'Swaziland',
  Sweden: 'Sverige',
  Switzerland: 'Sveits',
  Syria: 'Syria',
  Taiwan: 'Taiwan',
  Tajikistan: 'Tadsjikistan',
  Tanzania: 'Tanzania',
  Thailand: 'Thailand',
  'The Bahamas': 'Bahamas',
  Togo: 'Togo',
  Tokelau: 'Tokelau',
  Tonga: 'Tonga',
  'Trinidad And Tobago': 'Trinidad og Tobago',
  Tunisia: 'Tunisia',
  Turkey: 'Tyrkia',
  Turkmenistan: 'Turkmenistan',
  'Turks And Caicos Islands': 'Turks- og Caicosøyene',
  Tuvalu: 'Tuvalu',
  Uganda: 'Uganda',
  Ukraine: 'Ukraina',
  'United Arab Emirates': 'De forente arabiske emirater',
  'United Kingdom': 'Storbritannia',
  'United States': 'USA',
  'United States Minor Outlying Islands': 'USAs ytre småøyer',
  Uruguay: 'Uruguay',
  Uzbekistan: 'Usbekistan',
  Vanuatu: 'Vanuatu',
  'Vatican City State (Holy See)': 'Vatikanstaten (Den hellige stol)',
  Venezuela: 'Venezuela',
  Vietnam: 'Vietnam',
  'Virgin Islands (British)': 'Jomfruøyene (britiske)',
  'Virgin Islands (US)': 'Jomfruøyene (USA)',
  'Wallis And Futuna Islands': 'Wallis- og Futunaøyene',
  'Western Sahara': 'Vest-Sahara',
  Yemen: 'Jemen',
  Zambia: 'Zambia',
  Zimbabwe: 'Zimbabwe',
}
